/** Reject the upload when no bytes move for this long; progress events re-arm the timer. */
const STALL_TIMEOUT_MS = 60_000;

/** One retry covers a transient drop; a second identical failure is reported, not hidden. */
const NETWORK_RETRIES = 1;

export type WebLensUploadFailureKind = "network" | "stalled" | "http" | "file_changed";

/**
 * Typed failure so the caller can present a localized message. The Error message is a debug
 * string for logs only — creator-facing wording lives in the caller's message catalog.
 */
export class WebLensUploadError extends Error {
    readonly kind: WebLensUploadFailureKind;
    readonly status?: number;

    constructor(kind: WebLensUploadFailureKind, status?: number) {
        super(status === undefined ? `web-lens upload failed (${kind})` : `web-lens upload failed (HTTP ${status})`);
        this.name = "WebLensUploadError";
        this.kind = kind;
        this.status = status;
    }
}

/**
 * Whether the browser can still read the picked File. Chrome refuses to send a File whose bytes
 * changed on disk after the picker (net::ERR_UPLOAD_FILE_CHANGED) and reports it as a plain
 * network error — status 0, instantly — indistinguishable from a dropped connection except by
 * trying to read the file. A one-byte slice is enough to get the answer.
 */
export const isFileReadable = async (file: Blob): Promise<boolean> => {
    try {
        await file.slice(0, 1).arrayBuffer();
        return true;
    } catch {
        return false;
    }
};

const attemptPut = (
    url: string,
    file: File,
    maxBytes: number,
    onProgress?: (fraction: number) => void,
): Promise<void> =>
    new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open("PUT", url);
        xhr.setRequestHeader("Content-Type", "application/octet-stream");
        xhr.setRequestHeader("X-Goog-Content-Length-Range", `0,${maxBytes}`);

        // A dead connection can fire no events at all, which would hang this promise (and the
        // submit checklist) forever. Stall-based rather than total-duration so a slow-but-moving
        // 100MB upload is never cut off.
        let stallTimer: ReturnType<typeof setTimeout>;
        const armStallTimer = () => {
            clearTimeout(stallTimer);
            stallTimer = setTimeout(() => {
                xhr.abort();
                reject(new WebLensUploadError("stalled"));
            }, STALL_TIMEOUT_MS);
        };

        xhr.upload.addEventListener("progress", (event) => {
            armStallTimer();
            if (event.lengthComputable && onProgress) {
                // A 0-byte body reports total 0 — avoid NaN% in the checklist.
                onProgress(event.total === 0 ? 1 : event.loaded / event.total);
            }
        });
        xhr.addEventListener("loadend", () => {
            clearTimeout(stallTimer);
        });
        xhr.addEventListener("load", () => {
            if (xhr.status >= 200 && xhr.status < 300) {
                resolve();
            } else {
                reject(new WebLensUploadError("http", xhr.status));
            }
        });
        xhr.addEventListener("error", () => {
            reject(new WebLensUploadError("network"));
        });
        armStallTimer();
        xhr.send(file);
    });

/**
 * PUTs a file to a GCS V4 signed URL. XMLHttpRequest instead of fetch because only XHR
 * exposes upload progress. The Content-Type and X-Goog-Content-Length-Range headers must match
 * what the URL was signed with, byte for byte — the size cap is bound into the signature, so GCS
 * itself refuses an oversized upload.
 *
 * A network-level failure is first checked against the file itself: an unreadable file is
 * reported as `file_changed` (re-selecting is the only fix, so retrying would just loop); a
 * readable one gets a single retry for the transient-drop case.
 */
export const putToSignedUrl = async (
    url: string,
    file: File,
    maxBytes: number,
    onProgress?: (fraction: number) => void,
): Promise<void> => {
    for (let attempt = 0; ; attempt += 1) {
        try {
            await attemptPut(url, file, maxBytes, onProgress);
            return;
        } catch (error) {
            if (!(error instanceof WebLensUploadError) || error.kind !== "network") {
                throw error;
            }
            if (!(await isFileReadable(file))) {
                throw new WebLensUploadError("file_changed");
            }
            if (attempt >= NETWORK_RETRIES) {
                throw error;
            }
        }
    }
};
